import { type AggregationLevel, type GSRPCapacityReportModel } from 'Pages/Reports/GSRPCapacityReport.types'
import { aggregateReportData, calculateSummaryStats, getCapacityStatus, getUtilizationPercent } from 'Pages/Reports/GSRPCapacityReport.utils'

// Dynamic imports to handle pdfmake's type issues
// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfMake = require('pdfmake/build/pdfmake')
// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfFonts = require('pdfmake/build/vfs_fonts')

// Type definitions for pdfmake (since the module doesn't export them properly)
type Content = any
type TDocumentDefinitions = any

// Initialize pdfMake with fonts
pdfMake.vfs = pdfFonts.pdfMake.vfs

interface PDFMetadata {
  isdName: string
  timespanName: string
}

/**
 * Generate and download GSRP Capacity Report PDF
 */
export function generateGSRPCapacityPDF (
  reportData: GSRPCapacityReportModel[],
  aggregationLevel: AggregationLevel,
  metadata: PDFMetadata
): void {
  if (reportData.length === 0) {
    console.warn('No data to generate PDF')
    return
  }

  const docDefinition = createDocumentDefinition(reportData, aggregationLevel, metadata)
  pdfMake.createPdf(docDefinition).download(generateFilename(aggregationLevel, metadata.timespanName))
}

/**
 * Generate PDF filename
 */
function generateFilename (aggregationLevel: AggregationLevel, timespanName: string): string {
  const date = new Date().toISOString().split('T')[0]
  const levelName = aggregationLevel.charAt(0).toUpperCase() + aggregationLevel.slice(1)
  const cleanTimespan = timespanName.replace(/[^a-zA-Z0-9]/g, '_')
  return `GSRP_Capacity_Report_${levelName}_${cleanTimespan}_${date}.pdf`
}

/**
 * Create the complete PDF document definition
 */
function createDocumentDefinition (
  reportData: GSRPCapacityReportModel[],
  aggregationLevel: AggregationLevel,
  metadata: PDFMetadata
): TDocumentDefinitions {
  const summaryStats = calculateSummaryStats(reportData)
  const aggregatedData = aggregateReportData(reportData, aggregationLevel)

  return {
    pageSize: 'LETTER',
    pageMargins: [40, 60, 40, 60],

    header: (currentPage: number, pageCount: number) => {
      if (currentPage === 1) return null
      return {
        text: 'GSRP Capacity Report',
        alignment: 'center',
        margin: [40, 20, 40, 10],
        fontSize: 10,
        color: '#666'
      }
    },

    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        {
          text: `Generated: ${new Date().toLocaleDateString()}`,
          alignment: 'left',
          fontSize: 8,
          color: '#666',
          margin: [40, 0, 0, 0]
        },
        {
          text: `Page ${String(currentPage)} of ${String(pageCount)}`,
          alignment: 'right',
          fontSize: 8,
          color: '#666',
          margin: [0, 0, 40, 0]
        }
      ]
    }),

    content: [
      // Title
      {
        text: 'GSRP Capacity Report',
        style: 'title',
        alignment: 'center',
        margin: [0, 0, 0, 20]
      },

      // Metadata cards section
      {
        columns: [
          {
            stack: [
              { text: 'ISD', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: metadata.isdName, fontSize: 11, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'School Year', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: metadata.timespanName, fontSize: 11, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Generated', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: new Date().toLocaleDateString(), fontSize: 11, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Detail Level', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: aggregationLevel.charAt(0).toUpperCase() + aggregationLevel.slice(1), fontSize: 11, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA'
          }
        ],
        margin: [0, 0, 0, 20],
        columnGap: 10
      },

      // Summary section
      createSummarySection(summaryStats),

      // Data section
      {
        text: 'Report Preview',
        style: 'sectionHeader',
        margin: [0, 20, 0, 5]
      },
      {
        text: [
          { text: 'Color coding: ', fontSize: 9, color: '#666' },
          { text: 'Green (available), ', fontSize: 9, color: '#4DA4A4', bold: true },
          { text: 'Yellow (limited 80-99%), ', fontSize: 9, color: '#F9D423', bold: true },
          { text: 'Red (full 100%+)', fontSize: 9, color: '#E4536B', bold: true }
        ],
        margin: [0, 0, 0, 10]
      },

      ...createDataSection(reportData, aggregationLevel, aggregatedData)
    ],

    styles: {
      title: {
        fontSize: 20,
        bold: true,
        color: '#333'
      },
      sectionHeader: {
        fontSize: 14,
        bold: true,
        color: '#333'
      },
      providerHeader: {
        fontSize: 12,
        bold: true,
        color: '#333',
        margin: [0, 20, 0, 5]
      },
      facilityHeader: {
        fontSize: 11,
        bold: true,
        color: '#4A4A4A',
        margin: [15, 5, 0, 3]
      },
      tableHeader: {
        bold: true,
        fontSize: 10,
        color: '#333',
        fillColor: '#E1E1E1'
      }
    }
  }
}

/**
 * Create summary statistics section
 */
function createSummarySection (stats: ReturnType<typeof calculateSummaryStats>): Content {
  return {
    stack: [
      {
        text: 'Summary Statistics',
        style: 'sectionHeader',
        margin: [0, 0, 0, 10]
      },
      // First row of stat cards
      {
        columns: [
          {
            stack: [
              { text: 'Total Capacity', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.totalCapacity.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Active Seats', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.totalActiveSeats.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Open Seats', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.openCapacity.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0], color: '#4DA4A4' }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Utilization', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: `${stats.utilizationPercent}%`, fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA'
          }
        ],
        margin: [0, 0, 0, 10],
        columnGap: 10
      },
      // Second row of stat cards
      {
        columns: [
          {
            stack: [
              { text: 'Seeking Spots', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: (stats.totalPending + stats.totalWaitlisted).toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0], color: '#FF9800' },
              { text: '(Pending + Waitlisted)', fontSize: 7, color: '#999', alignment: 'center', margin: [0, 2, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Pending', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.totalPending.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Waitlisted', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.totalWaitlisted.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA',
            margin: [0, 0, 3, 0]
          },
          {
            stack: [
              { text: 'Providers', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.providerCount.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: '*',
            fillColor: '#FAFAFA'
          }
        ],
        margin: [0, 0, 0, 10],
        columnGap: 10
      },
      // Third row with just Facilities
      {
        columns: [
          {
            stack: [
              { text: 'Facilities', fontSize: 9, color: '#666', bold: true, alignment: 'center' },
              { text: stats.facilityCount.toString(), fontSize: 16, bold: true, alignment: 'center', margin: [0, 4, 0, 0] }
            ],
            width: 128,
            fillColor: '#FAFAFA'
          },
          { width: '*', text: '' }
        ],
        margin: [0, 0, 0, 15],
        columnGap: 10
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 0,
            x2: 515,
            y2: 0,
            lineWidth: 1,
            lineColor: '#E1E1E1'
          }
        ],
        margin: [0, 5, 0, 0]
      }
    ]
  }
}

/**
 * Calculate capacity metrics for PDF rendering
 */
interface CapacityMetrics {
  openSeats: number
  statusInfo: ReturnType<typeof getCapacityStatus>
  utilization: number
  seeking: number
  progressWidth: number
  filledWidth: number
}

function calculateCapacityMetrics (
  row: { totalAllocatedCapacity: number, totalActiveSeats: number, countPendingProcessing: number, countWaitlisted: number },
  progressWidth: number
): CapacityMetrics {
  const openSeats = Math.max(0, row.totalAllocatedCapacity - row.totalActiveSeats)
  const statusInfo = getCapacityStatus(row.totalActiveSeats, row.totalAllocatedCapacity)
  const utilization = getUtilizationPercent(row.totalActiveSeats, row.totalAllocatedCapacity)
  const seeking = row.countPendingProcessing + row.countWaitlisted
  const filledWidth = Math.min((utilization / 100) * progressWidth, progressWidth)

  return {
    openSeats,
    statusInfo,
    utilization,
    seeking,
    progressWidth,
    filledWidth
  }
}

/**
 * Create data section based on aggregation level
 */
function createDataSection (
  reportData: GSRPCapacityReportModel[],
  aggregationLevel: AggregationLevel,
  aggregatedData: ReturnType<typeof aggregateReportData>
): Content[] {
  if (aggregationLevel === 'provider') {
    return [createProviderLevelTable(aggregatedData)]
  } else {
    return createFacilityLevelContent(reportData, aggregatedData)
  }
}

/**
 * Create provider-level cards
 */
function createProviderLevelTable (data: ReturnType<typeof aggregateReportData>): Content {
  const cards: any[] = []

  data.forEach(row => {
    const { openSeats, statusInfo, utilization, seeking, progressWidth, filledWidth } = calculateCapacityMetrics(row, 480)

    cards.push({
      stack: [
        // Provider name
        { text: row.providerName, fontSize: 11, bold: true, color: '#333', margin: [0, 0, 0, 5] },

        // Utilization bar with label
        {
          columns: [
            { text: 'Utilization', fontSize: 7, bold: true, color: '#666', width: 60 },
            { text: `${utilization}%`, fontSize: 7, bold: true, color: statusInfo.color, alignment: 'right', width: '*' }
          ],
          margin: [0, 0, 0, 2]
        },

        // Progress bar
        {
          canvas: [
            // Background bar
            { type: 'rect', x: 0, y: 0, w: progressWidth, h: 8, r: 1, color: '#E1E1E1' },
            // Filled portion
            { type: 'rect', x: 0, y: 0, w: filledWidth, h: 8, r: 1, color: statusInfo.color }
          ],
          margin: [0, 0, 0, 5]
        },

        // Stats grid
        {
          columns: [
            {
              stack: [
                { text: 'Capacity', fontSize: 7, color: '#666', alignment: 'center' },
                { text: row.totalAllocatedCapacity.toString(), fontSize: 11, bold: true, alignment: 'center', margin: [0, 1, 0, 0] }
              ],
              width: '*',
              fillColor: '#FAFAFA',
              margin: [0, 0, 2, 0]
            },
            {
              stack: [
                { text: 'Active', fontSize: 7, color: '#666', alignment: 'center' },
                { text: row.totalActiveSeats.toString(), fontSize: 11, bold: true, alignment: 'center', margin: [0, 1, 0, 0] }
              ],
              width: '*',
              fillColor: '#FAFAFA',
              margin: [0, 0, 2, 0]
            },
            {
              stack: [
                { text: 'Open', fontSize: 7, color: '#666', alignment: 'center' },
                { text: openSeats.toString(), fontSize: 11, bold: true, alignment: 'center', margin: [0, 1, 0, 0], color: '#4DA4A4' }
              ],
              width: '*',
              fillColor: '#FAFAFA',
              margin: [0, 0, 2, 0]
            },
            {
              stack: [
                { text: 'Seeking', fontSize: 7, color: '#666', alignment: 'center' },
                { text: seeking.toString(), fontSize: 11, bold: true, alignment: 'center', margin: [0, 1, 0, 0], color: '#FF9800' }
              ],
              width: '*',
              fillColor: '#FAFAFA'
            }
          ],
          columnGap: 4
        }
      ],
      margin: [8, 6, 8, 6],
      fillColor: '#FFFFFF',
      border: [true, true, true, true],
      borderColor: [statusInfo.color, statusInfo.color, statusInfo.color, statusInfo.color]
    })
  })

  return { stack: cards, margin: [0, 0, 0, 5] }
}

/**
 * Create facility-level content with provider grouping
 */
function createFacilityLevelContent (
  reportData: GSRPCapacityReportModel[],
  aggregatedData: ReturnType<typeof aggregateReportData>
): Content[] {
  const content: Content[] = []

  // Group aggregated data by provider
  const byProvider = aggregatedData.reduce<Record<string, typeof aggregatedData>>((acc, row) => {
    if (acc[row.providerName] === undefined) {
      acc[row.providerName] = []
    }
    acc[row.providerName].push(row)
    return acc
  }, {})

  Object.entries(byProvider).forEach(([providerName, facilities]) => {
    content.push({
      text: providerName,
      fontSize: 11,
      bold: true,
      color: '#333',
      margin: [0, 10, 0, 5]
    })

    const facilityCards: any[] = []

    facilities.forEach(facility => {
      const { openSeats, statusInfo, utilization, seeking, progressWidth, filledWidth } = calculateCapacityMetrics(facility, 120)

      facilityCards.push({
        columns: [
          // Left side: Facility name and progress bar
          {
            stack: [
              { text: facility.facilityName ?? '', fontSize: 9, bold: true, color: '#333', margin: [0, 0, 0, 2] },
              {
                columns: [
                  {
                    canvas: [
                      // Background bar
                      { type: 'rect', x: 0, y: 0, w: progressWidth, h: 5, r: 1, color: '#E1E1E1' },
                      // Filled portion
                      { type: 'rect', x: 0, y: 0, w: filledWidth, h: 5, r: 1, color: statusInfo.color }
                    ],
                    width: progressWidth
                  },
                  { text: `${utilization}%`, fontSize: 7, bold: true, color: statusInfo.color, margin: [3, -1, 0, 0], width: 'auto' }
                ]
              }
            ],
            width: 145
          },

          // Right side: Stats
          {
            columns: [
              {
                stack: [
                  { text: 'Cap', fontSize: 6, color: '#666', alignment: 'center' },
                  { text: facility.totalAllocatedCapacity.toString(), fontSize: 9, bold: true, alignment: 'center', margin: [0, 1, 0, 0] }
                ],
                width: 'auto'
              },
              {
                stack: [
                  { text: 'Act', fontSize: 6, color: '#666', alignment: 'center' },
                  { text: facility.totalActiveSeats.toString(), fontSize: 9, bold: true, alignment: 'center', margin: [0, 1, 0, 0] }
                ],
                width: 'auto'
              },
              {
                stack: [
                  { text: 'Open', fontSize: 6, color: '#4DA4A4', alignment: 'center' },
                  { text: openSeats.toString(), fontSize: 9, bold: true, alignment: 'center', margin: [0, 1, 0, 0], color: '#4DA4A4' }
                ],
                width: 'auto'
              },
              {
                stack: [
                  { text: 'Seek', fontSize: 6, color: '#FF9800', alignment: 'center' },
                  { text: seeking.toString(), fontSize: 9, bold: true, alignment: 'center', margin: [0, 1, 0, 0], color: '#FF9800' }
                ],
                width: 'auto'
              }
            ],
            width: '*',
            columnGap: 8
          }
        ],
        columnGap: 12,
        margin: [10, 4, 4, 4],
        fillColor: '#FFFFFF'
      })
    })

    content.push({
      stack: facilityCards,
      margin: [0, 0, 0, 8]
    })
  })

  return content
}
